/**
 * useOrgUnits Hook
 *
 * **V28 변경: Firestore 직접 조회 → Cloud Function 경유**
 * - getOrgUnitsForAdmin Cloud Function 호출
 * - 서버에서 회사 스코프(companyCode) 필터링 적용
 * - system_admin: 전체 조직 조회
 * - role_admin_kr: KR 회사 조직만 조회
 * - role_admin_cn: CN 회사 조직만 조회
 *
 * **역할:**
 * - Cloud Function을 통해 조직 목록을 조회하는 React Hook
 * - 로딩/에러 상태 관리
 * - 클라이언트 사이드 필터링 지원 (name, type, isActive, parentId)
 *
 * **사용 예시:**
 * ```tsx
 * function HrOrgUnitsPage() {
 *   const { orgUnits, isLoading, error } = useOrgUnits({
 *     name: '개발',
 *     type: 'team',
 *     isActive: true
 *   });
 *
 *   if (isLoading) return <div>로딩 중...</div>;
 *   if (error) return <div>에러: {error.message}</div>;
 *
 *   return (
 *     <ul>
 *       {orgUnits.map(org => (
 *         <li key={org.id}>{org.name}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../firebase/config';
import {
  OrgUnit,
  OrgUnitListFilter,
  mapOrgUnitDocToOrgUnit,
} from '../types/orgUnit';
import { getCached, setCached, CACHE_KEY } from '../utils/masterDataCache';

/**
 * V29.15: getOrgUnitsForAdmin 요청 타입
 */
interface GetOrgUnitsForAdminRequest {
  requestedCompanyCode?: 'KR' | 'CN' | 'ALL';
}

/**
 * V29.15: OrgUnitListFilter에 requestedCompanyCode 추가
 */
export interface OrgUnitListFilterExtended extends OrgUnitListFilter {
  requestedCompanyCode?: 'KR' | 'CN' | 'ALL';
}

/**
 * V28: getOrgUnitsForAdmin 응답 타입
 * V154.1: approverEmployeeId, approverName 추가
 */
interface GetOrgUnitsForAdminResponse {
  success: boolean;
  orgUnits: Array<{
    id: string;
    name: string;
    code: string;
    type: string;
    parentId: string | null;
    status: string;
    order: number;
    level: number;
    path: string;
    companyCode?: string;
    // V154.1: 승인자 정보
    approverEmployeeId?: string;
    approverName?: string;
    createdAt?: any;
    updatedAt?: any;
  }>;
  total: number;
  scope: string;
}

/**
 * useOrgUnits Hook 반환 타입
 */
export interface UseOrgUnitsResult {
  /**
   * 조직 목록 (필터링 적용 후)
   */
  orgUnits: OrgUnit[];

  /**
   * 로딩 중 여부
   */
  isLoading: boolean;

  /**
   * 에러 정보
   */
  error: Error | null;

  /**
   * 데이터 재조회 함수
   *
   * 조직 추가/수정/삭제 후 목록을 다시 불러올 때 사용
   */
  refetch: () => Promise<void>;

  /**
   * V28: 조회 스코프 (ALL, KR, CN 등)
   */
  scope: string | null;
}

/**
 * useOrgUnits Hook
 *
 * @param filters 조직 필터링 조건 (선택 사항)
 * @returns 조직 목록, 로딩 상태, 에러 정보, 재조회 함수
 *
 * **V28 동작 원리:**
 * 1. getOrgUnitsForAdmin Cloud Function 호출
 * 2. 서버에서 회사 스코프 필터 적용됨 (system_admin=ALL, role_admin_kr=KR, role_admin_cn=CN)
 * 3. filters가 있으면 클라이언트 사이드에서 추가 필터링
 * 4. 필터링된 결과를 orgUnits로 반환
 *
 * **성능 고려사항:**
 * - 서버에서 회사 스코프 필터 적용 후 클라이언트 필터링
 * - 조직 개수가 적으므로 (일반적으로 10~100개) 성능 문제 없음
 */
export function useOrgUnits(filters?: OrgUnitListFilterExtended): UseOrgUnitsResult {
  // ============================================
  // 상태 관리
  // ============================================

  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [scope, setScope] = useState<string | null>(null);

  // ============================================
  // 조직 목록 조회 함수
  // ============================================

  /**
   * V28: Cloud Function을 통해 조직 목록 조회
   */
  const fetchOrgUnits = async () => {
    // Firebase가 초기화되지 않은 경우 (DEMO 모드)
    if (!functions) {
      setOrgUnits([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // V259: 캐시 키 — requestedCompanyCode만 BE 파라미터이므로 이것만 키에 포함
      const cacheKey = `${CACHE_KEY.ORG_UNITS}${filters?.requestedCompanyCode || 'default'}`;
      let allOrgUnits: OrgUnit[];
      let cachedScope: string | null = null;

      // V259: 캐시 적중 시 callable 스킵
      const cached = getCached<{ orgUnits: OrgUnit[]; scope: string | null }>(cacheKey);
      if (cached) {
        allOrgUnits = cached.orgUnits;
        cachedScope = cached.scope;
      } else {
        // V28: Cloud Function 호출, V29.15: requestedCompanyCode 추가
        const getOrgUnitsForAdmin = httpsCallable<GetOrgUnitsForAdminRequest, GetOrgUnitsForAdminResponse>(
          functions,
          'getOrgUnitsForAdmin'
        );

        const result = await getOrgUnitsForAdmin({
          requestedCompanyCode: filters?.requestedCompanyCode,
        });
        const response = result.data;

        if (!response.success) {
          throw new Error('조직 목록 조회 실패');
        }

        allOrgUnits = response.orgUnits.map((org) => ({
          id: org.id,
          name: org.name,
          code: org.code,
          type: org.type as OrgUnit['type'],
          parentId: org.parentId,
          status: org.status as OrgUnit['status'],
          order: org.order,
          level: org.level as OrgUnit['level'],
          path: org.path,
          companyCode: org.companyCode as OrgUnit['companyCode'],
          approverEmployeeId: org.approverEmployeeId,
          approverName: org.approverName,
          createdAt: org.createdAt,
          updatedAt: org.updatedAt,
          createdBy: '',
          updatedBy: '',
        }));
        cachedScope = response.scope;

        // V259: 캐시 저장
        setCached(cacheKey, { orgUnits: allOrgUnits, scope: cachedScope });
      }

      setScope(cachedScope);

      // 클라이언트 사이드 필터링
      let results = [...allOrgUnits];

      if (filters) {
        if (filters.name) {
          const searchName = filters.name.toLowerCase();
          results = results.filter((org) =>
            org.name.toLowerCase().includes(searchName)
          );
        }
        if (filters.type) {
          results = results.filter((org) => org.type === filters.type);
        }
        if (filters.isActive !== undefined) {
          if (filters.isActive) {
            results = results.filter((org) => org.status === 'active');
          } else {
            results = results.filter((org) => org.status !== 'active');
          }
        }
        if (filters.parentId !== undefined) {
          results = results.filter((org) => org.parentId === filters.parentId);
        }
      }

      setOrgUnits(results);
    } catch (err: any) {
      console.error('[useOrgUnits] V28 조직 목록 조회 실패:', err);

      if (err?.code === 'functions/permission-denied') {
        setError(new Error('조직 목록 조회 권한이 없습니다.'));
      } else if (err?.code === 'functions/unauthenticated') {
        setError(new Error('로그인이 필요합니다.'));
      } else {
        setError(err instanceof Error ? err : new Error('조직 목록 조회 중 오류가 발생했습니다.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // 초기 로딩 (컴포넌트 마운트 시)
  // ============================================

  useEffect(() => {
    fetchOrgUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // filters의 각 필드가 변경될 때마다 재조회
    filters?.name,
    filters?.type,
    filters?.isActive,
    filters?.parentId,
    filters?.requestedCompanyCode, // V29.15: 국가 필터 변경 시 재조회
  ]);

  // ============================================
  // 반환
  // ============================================

  return {
    orgUnits,
    isLoading,
    error,
    refetch: fetchOrgUnits,
    scope,
  };
}

/**
 * ============================================
 * useOrgUnit Hook (단건 조회) - Chapter 4-2
 * ============================================
 */

/**
 * useOrgUnit Hook 반환 타입
 */
export interface UseOrgUnitResult {
  /**
   * 조직 정보 (조회 성공 시)
   */
  orgUnit: OrgUnit | null;

  /**
   * 로딩 중 여부
   */
  isLoading: boolean;

  /**
   * 에러 정보
   */
  error: Error | null;

  /**
   * 데이터 재조회 함수
   */
  refetch: () => Promise<void>;
}

/**
 * useOrgUnit Hook
 *
 * @param orgUnitId 조회할 조직 ID
 * @returns 조직 정보, 로딩 상태, 에러 정보, 재조회 함수
 *
 * **사용 예시:**
 * ```tsx
 * function OrgUnitDetailPanel({ orgUnitId }: { orgUnitId: string }) {
 *   const { orgUnit, isLoading, error, refetch } = useOrgUnit(orgUnitId);
 *
 *   if (isLoading) return <div>로딩 중...</div>;
 *   if (error) return <div>에러: {error.message}</div>;
 *   if (!orgUnit) return <div>조직을 찾을 수 없습니다.</div>;
 *
 *   return <div>{orgUnit.name}</div>;
 * }
 * ```
 */
export function useOrgUnit(orgUnitId: string | undefined): UseOrgUnitResult {
  const [orgUnit, setOrgUnit] = useState<OrgUnit | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Firestore에서 조직 정보 조회
   * Note: 단건 조회는 현재 Firestore 직접 조회 유지
   * (향후 필요시 Cloud Function 전환 가능)
   */
  const fetchOrgUnit = async () => {
    // orgUnitId가 없는 경우
    if (!orgUnitId) {
      setOrgUnit(null);
      setIsLoading(false);
      return;
    }

    // Firebase가 초기화되지 않은 경우 (DEMO 모드)
    if (!db) {
      setOrgUnit(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Firestore에서 orgUnits 문서 조회
      // Note: db는 이미 위에서 null 체크되었으므로 ! 사용
      const { doc, getDoc } = await import('firebase/firestore');
      const docRef = doc(db!, 'orgUnits', orgUnitId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        // 문서를 OrgUnit 타입으로 변환
        const orgUnitData = mapOrgUnitDocToOrgUnit(docSnap as any);
        setOrgUnit(orgUnitData);
      } else {
        // 문서가 존재하지 않음
        console.warn(`[useOrgUnit] 조직을 찾을 수 없습니다: ${orgUnitId}`);
        setOrgUnit(null);
      }
    } catch (err) {
      console.error('[useOrgUnit] 조직 조회 실패:', err);
      setError(err instanceof Error ? err : new Error('조직 조회 중 오류가 발생했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  // 초기 로딩 (컴포넌트 마운트 시 + orgUnitId 변경 시)
  useEffect(() => {
    fetchOrgUnit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgUnitId]);

  return {
    orgUnit,
    isLoading,
    error,
    refetch: fetchOrgUnit,
  };
}

/**
 * ============================================
 * V28 변경 요약
 * ============================================
 *
 * **변경 전:**
 * - Firestore orgUnits 컬렉션 직접 getDocs 호출
 * - Firestore Rules 권한으로 접근 제어
 * - 클라이언트에서 전체 데이터 로드 후 필터링
 *
 * **변경 후:**
 * - getOrgUnitsForAdmin Cloud Function 호출
 * - 서버에서 회사 스코프 필터 적용
 *   - system_admin: 전체 (ALL)
 *   - role_admin_kr: KR 회사만
 *   - role_admin_cn: CN 회사만
 * - 클라이언트에서 추가 필터링 (name, type, isActive, parentId)
 *
 * **장점:**
 * - Firestore Rules 권한 오류 해결
 * - 회사 스코프 필터링 서버에서 강제 적용
 * - SSoT (auth_roleAssignments 기반) 원칙 준수
 */
